// import { convertPointArrayToArray } from '@/src/store/contours/contour-processing/convertArrayToPointArray';
import type { ContourId } from '@/src/store/contours/contour-definitions';

import { tracePointPyramidalLK } from '@src/store/contours/contour-processing/opticalPointTrace';
import { useCurrentStudyStore } from '@src/store/dicom-web/current-study-store';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { useAlert } from '@/src/composables/useAlert';

import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import {
  ContourLabel,
  encodeContourLabelItemId,
} from '@/src/store/contours/contour-definitions';

function getStartingPointCoordinates(
  currentImageUID: string,
  targetPointLabel: ContourId,
): { x: number; y: number } | null {
  const contourContainerStore = useContourContainerStore();
  const contours
    = contourContainerStore.getDrawingContours(currentImageUID) || null;

  if (!contours || Object.keys(contours).length === 0) {
    console.warn('No contours found for image UID:', currentImageUID);
    return null;
  }

  // Target targetPointLabel:
  const { label, id } = targetPointLabel;
  if (!contours[label] || !contours[label][id]) {
    console.warn(
      `No contour found for label ${label} with id ${id} in image UID:`,
      currentImageUID,
    );
    return null;
  }

  const targetContour = contours[label][id];
  //   const scaledContour = convertPointArrayToArray(
  //     targetContour.getPolygonScaled()
  //   );
  const scaledContour = targetContour.getPolygonScaled();
  const firstPoint = scaledContour[0];

  if (!firstPoint) {
    console.warn(
      `Contour for label ${label} with id ${id} is empty in image UID:`,
      currentImageUID,
    );
    return null;
  }

  return firstPoint;
}

export async function usePointTracing(
  viewerID: string,
  currentImageUID: string,
  targetPointLabel: ContourId = {
    label: ContourLabel.RvInsertion,
    id: encodeContourLabelItemId([0]),
  },
) {
  const currentStudyStore = useCurrentStudyStore();
  const contourContainerStore = useContourContainerStore();
  const alert = useAlert();
  const startTime = performance.now();
  const studyObj = useViewportActions().getStudyObjectByViewerId(viewerID);
  const breathholdState = studyObj?.getBreathhold();
  const start = breathholdState?.getStart();
  const end = breathholdState?.getEnd();

  const startingPointCoordinates = getStartingPointCoordinates(
    currentImageUID,
    targetPointLabel,
  );
  if (!startingPointCoordinates) return;

  const imageUIDsfromStore
    = currentStudyStore.getSliceImageUIDsbyView(viewerID);

  const croppedImageUIDs = imageUIDsfromStore.slice(start, end && end + 1);

  const startingImageIndex = croppedImageUIDs.indexOf(currentImageUID);
  if (startingImageIndex === -1) {
    alert.error('Cannot run trace. Current image UID not found in store');
    return;
  }

  const rotatedImageUIDs = [
    ...croppedImageUIDs.slice(startingImageIndex),
    ...croppedImageUIDs.slice(0, startingImageIndex),
  ];

  const imageData = [];
  const dimensions = [];
  for (const imageUID of rotatedImageUIDs) {
    const imageMeta = await currentStudyStore.fetchOrGetVtkImageData(imageUID);
    if (!imageMeta) {
      alert.error(
        'Cannot run trace. Unable to retrieve image data for one or more images in the current view.',
      );
      return;
    }
    const pixelIntensity = imageMeta.getPointData().getScalars().getData();
    const dimensionsTemp = imageMeta.getDimensions();
    imageData.push(pixelIntensity);
    if (dimensions.length === 0) {
      dimensions.push(dimensionsTemp[1], dimensionsTemp[0]); // opticalPointTrace expects [height, width]
    }
  }

  const tracedPoints = tracePointPyramidalLK(
    startingPointCoordinates,
    0,
    imageData,
    dimensions as [number, number],
  );

  //   Add contours to store
  const id = encodeContourLabelItemId([0]);
  const batchContours = [];
  for (const trace of tracedPoints) {
    const { frameIndex, point } = trace;
    const imageUID = rotatedImageUIDs[frameIndex];
    batchContours.push({
      UID: imageUID,
      points: [point],
      label: targetPointLabel.label,
      id,
    });
  }
  contourContainerStore.addContoursBatch(batchContours);
  const endTime = performance.now();
  alert.success(
    `Point tracing completed in ${((endTime - startTime) / 1000).toFixed(
      2,
    )} seconds!`,
  );
}
